import {
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { FormField } from '@angular/forms/signals';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { LucideModule } from '../../../../../shared/modules/lucide.module';
import { FORMAS_RESPOSTA, UFS } from '../../models/manifestacao-publica.model';
import { OuvidoriaPublicaService } from '../../services/ouvidoria-publica.service';
import { CampoHoneypotComponent } from '../../../../../shared/components/campo-honeypot/campo-honeypot.component';
import { LIMITE_ASSUNTO, LIMITE_DESCRICAO, ManifestacaoStore } from '../manifestacao.store';

@Component({
  selector: 'app-etapa-solicitacao-ouvidoria',
  templateUrl: './etapa-solicitacao-ouvidoria.component.html',
  styleUrls: ['./etapa-solicitacao-ouvidoria.component.css'],
  imports: [LucideModule, FormField, NgxMaskDirective, CampoHoneypotComponent],
  // O modelo guarda o valor mascarado, do mesmo jeito que o cadastro do perfil entrega.
  providers: [provideNgxMask({ dropSpecialCharacters: false })],
})
export class EtapaSolicitacaoOuvidoriaComponent {
  /** O formulário mora no store: a etapa é recriada a cada troca de passo, os dados não. */
  protected readonly store = inject(ManifestacaoStore);

  /** O canal do órgão diz o que a tela pode oferecer: sigilo e anexo somem quando ele não permite. */
  protected readonly ouvidoria = inject(OuvidoriaPublicaService);

  private readonly injector = inject(Injector);

  readonly rotuloDescricao = input('Descrição da manifestação');
  readonly placeholderDescricao = input('Descreva sua manifestação...');

  readonly limiteDescricao = LIMITE_DESCRICAO;
  readonly limiteAssunto = LIMITE_ASSUNTO;

  /** Máscara nativa do ngx-mask: alterna entre CPF e CNPJ conforme os dígitos digitados. */
  readonly mascaraDocumento = 'CPF_CNPJ';
  readonly mascaraTelefone = '(00) 0000-0000||(00) 00000-0000';

  readonly ufs = UFS;
  readonly formasResposta = FORMAS_RESPOSTA;

  readonly arrastando = signal(false);

  readonly mostrarPendencias = signal(false);

  /** O bloco de erro da API precisa receber o foco: é assim que o leitor de tela o anuncia. */
  private readonly blocoErro = viewChild<ElementRef<HTMLElement>>('blocoErro');

  constructor() {
    effect(() => {
      if (!this.store.erroEnvio()) return;

      afterNextRender(() => this.blocoErro()?.nativeElement.focus(), { injector: this.injector });
    });
  }

  destacarPendencias(): void {
    this.mostrarPendencias.set(true);
    this.store.solicitacaoForm().markAsTouched();
  }

  aoSelecionar(evento: Event): void {
    const campo = evento.target as HTMLInputElement;
    this.store.adicionarArquivos(campo.files);
    campo.value = '';
  }

  aoArrastar(evento: DragEvent): void {
    evento.preventDefault();
    this.arrastando.set(true);
  }

  aoSairDaArea(): void {
    this.arrastando.set(false);
  }

  aoSoltar(evento: DragEvent): void {
    evento.preventDefault();
    this.arrastando.set(false);
    this.store.adicionarArquivos(evento.dataTransfer?.files);
  }

  remover(id: number): void {
    this.store.removerArquivo(id);
  }

  // Os três booleanos escrevem direto no modelo: o `formField` do Signal Forms liga `value`, e
  // caixa de seleção precisa de `checked`.
  alternarDesejaResposta(evento: Event): void {
    const marcado = (evento.target as HTMLInputElement).checked;
    this.store.dados.update((atual) => ({ ...atual, desejaResposta: marcado }));
  }

  alternarSigilo(evento: Event): void {
    const marcado = (evento.target as HTMLInputElement).checked;
    this.store.dados.update((atual) => ({ ...atual, sigiloIdentidade: marcado }));
  }

  alternarAceite(evento: Event): void {
    const marcado = (evento.target as HTMLInputElement).checked;
    this.store.dados.update((atual) => ({ ...atual, aceiteComunicacao: marcado }));
  }

  formatarTamanho(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
